/**
 * Production-safe logger utility
 * Provides debug logging that works in production when enabled via URL parameter
 */

interface LogEntry {
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  component: string;
  message: string;
  data?: any;
}

class Logger {
  private static instance: Logger;
  private debugMode: boolean = false;
  private logs: LogEntry[] = [];
  private maxLogs: number = 100;

  private constructor() {
    // Check if debug mode is enabled via URL parameter
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      this.debugMode = urlParams.get('debug') === 'true' || 
                      process.env.NODE_ENV === 'development';
      
      // Store debug mode in sessionStorage to persist across navigation
      if (this.debugMode) {
        sessionStorage.setItem('debug_mode', 'true');
      } else if (sessionStorage.getItem('debug_mode') === 'true') {
        this.debugMode = true;
      }
    }
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private log(level: LogEntry['level'], component: string, message: string, data?: any) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      component,
      message,
      data
    };

    // Store in memory
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Output to console if debug mode is enabled
    if (this.debugMode || level === 'error' || level === 'warn') {
      const consoleMethod = level === 'error' ? console.error : 
                           level === 'warn' ? console.warn : 
                           console.log;
      
      consoleMethod(
        `[${entry.timestamp}] [${component}] ${message}`,
        data ? data : ''
      );
    }

    // Store critical errors in localStorage for later analysis
    if (level === 'error' && typeof window !== 'undefined') {
      try {
        const errors = JSON.parse(localStorage.getItem('app_errors') || '[]');
        errors.push(entry);
        // Keep only last 10 errors
        if (errors.length > 10) {
          errors.shift();
        }
        localStorage.setItem('app_errors', JSON.stringify(errors));
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }

  debug(component: string, message: string, data?: any) {
    this.log('debug', component, message, data);
  }

  info(component: string, message: string, data?: any) {
    this.log('info', component, message, data);
  }

  warn(component: string, message: string, data?: any) {
    this.log('warn', component, message, data);
  }

  error(component: string, message: string, data?: any) {
    this.log('error', component, message, data);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
  }

  isDebugMode(): boolean {
    return this.debugMode;
  }

  // Export logs as JSON for debugging
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Export a hook for React components
export function useLogger(component: string) {
  return {
    debug: (message: string, data?: any) => logger.debug(component, message, data),
    info: (message: string, data?: any) => logger.info(component, message, data),
    warn: (message: string, data?: any) => logger.warn(component, message, data),
    error: (message: string, data?: any) => logger.error(component, message, data),
  };
}